import { useCallback, useMemo } from "react";
import { useLivePreview } from "@payloadcms/live-preview-react";
import { getPayloadCmsUrl } from "src/api/payload/client";
import { isPayloadSource } from "src/utils/hubSource";

/**
 * Generic live preview for a single hub card (admin iframe preview pages).
 *
 * @param {string} moduleName - e.g. `promotions`, `egd`, `entertainment`
 * @param {Object} config
 * @param {number} config.depth - Payload query depth for the live-preview relation fetch
 * @param {(liveDoc: Object, locale?: string) => Object} config.mapDoc - Maps the raw Payload doc into this module's card shape
 * @param {Function} config.requestHandler - Payload live-preview request handler for this module's collection
 * @param {(liveDoc: Object) => unknown} [config.getDocId] - Returns the doc's unique id used to detect a live document; defaults to `(doc) => doc?.key`
 * @param {string} [locale] - `en` | `fr`
 * @returns {{
 *   card: Object | null,
 *   isLoading: boolean,
 *   isLivePreview: boolean,
 * }}
 */
export function useHubCardLivePreview(moduleName, config, locale) {
  const { depth, mapDoc, requestHandler, getDocId = (doc) => doc?.key } = config;
  const enabled = isPayloadSource(moduleName);
  const initialDoc = useMemo(() => ({}), []);
  const stableRequestHandler = useCallback(requestHandler, [requestHandler]);

  const { data: liveDoc, isLoading } = useLivePreview({
    initialData: initialDoc,
    serverURL: getPayloadCmsUrl(),
    depth,
    requestHandler: stableRequestHandler,
  });

  const docId = getDocId(liveDoc);

  const card = useMemo(() => {
    if (!enabled || !docId) return null;
    return mapDoc(liveDoc, locale);
  }, [enabled, liveDoc, locale, docId, mapDoc]);

  return {
    card,
    isLoading: enabled && isLoading,
    isLivePreview: Boolean(docId),
  };
}
